/**
 * Interview Duration Calculator Utility (Frontend).
 *
 * Mirrors backend calculation in interviews.utils.duration_calculator.
 * Used for instant client-side calculation during question creation/editing
 * and schedule invite modal configuration.
 */

export const SESSION_TRANSITION_BUFFER_MINUTES = 2.5;
export const DEFAULT_THEORETICAL_MINUTES = 2.0;
export const DEFAULT_CONCEPTUAL_MINUTES = 2.0;
export const DEFAULT_BEHAVIORAL_MINUTES = 2.5;
export const DEFAULT_GENERAL_MINUTES = 2.0;
export const DEFAULT_MCQ_MINUTES = 1.5;
export const DEFAULT_CODING_MINUTES = 5.0;
export const MINIMUM_ABSOLUTE_DURATION_MINUTES = 15;

/**
 * Calculates the required minimum and recommended interview duration for a list of questions.
 *
 * @param {Array} questions - Array of question objects or strings
 * @returns {Object} { total_questions, minimum_duration_minutes, recommended_duration_minutes, breakdown }
 */
export function calculateInterviewDurationRequirements(questions = []) {
  if (!questions || !Array.isArray(questions) || questions.length === 0) {
    return {
      total_questions: 0,
      minimum_duration_minutes: MINIMUM_ABSOLUTE_DURATION_MINUTES,
      recommended_duration_minutes: 30,
      breakdown: {
        coding_questions: 0,
        coding_allocated_minutes: 0,
        mcq_questions: 0,
        concept_questions: 0,
        behavioral_questions: 0,
        general_questions: 0,
        theoretical_questions: 0,
        transition_buffer_minutes: SESSION_TRANSITION_BUFFER_MINUTES,
        exact_required_minutes: SESSION_TRANSITION_BUFFER_MINUTES,
      },
    };
  }

  let codingCount = 0;
  let codingMinutes = 0;
  let mcqCount = 0;
  let conceptCount = 0;
  let behavioralCount = 0;
  let generalCount = 0;
  let totalTime = SESSION_TRANSITION_BUFFER_MINUTES;

  for (const q of questions) {
    if (typeof q === 'object' && q !== null) {
      const type = (q.type || q.question_type || 'concept').toString().toLowerCase().trim();
      if (type === 'coding') {
        codingCount += 1;
        const rawLimit = q.time_limit ?? q.timeLimit;
        const limitVal = (rawLimit != null && !isNaN(Number(rawLimit)) && Number(rawLimit) > 0)
          ? Number(rawLimit)
          : DEFAULT_CODING_MINUTES;
        codingMinutes += limitVal;
        totalTime += limitVal;
      } else if (type === 'mcq') {
        mcqCount += 1;
        totalTime += DEFAULT_MCQ_MINUTES;
      } else if (type === 'behavioral' || type === 'behavioural') {
        behavioralCount += 1;
        totalTime += DEFAULT_BEHAVIORAL_MINUTES;
      } else if (type === 'general' || type === 'other') {
        generalCount += 1;
        totalTime += DEFAULT_GENERAL_MINUTES;
      } else {
        conceptCount += 1;
        totalTime += DEFAULT_CONCEPTUAL_MINUTES;
      }
    } else {
      conceptCount += 1;
      totalTime += DEFAULT_CONCEPTUAL_MINUTES;
    }
  }

  const rawMin = Math.ceil(totalTime);
  const minDuration = Math.max(MINIMUM_ABSOLUTE_DURATION_MINUTES, rawMin);

  // Recommended duration:
  // For very small question sets (<= 10 mins needed), 15 mins is already plenty of time.
  // Otherwise, round up to the next 5-minute increment above minDuration to ensure a comfortable slot.
  let recommendedDuration;
  if (totalTime <= 10.0) {
    recommendedDuration = Math.max(MINIMUM_ABSOLUTE_DURATION_MINUTES, minDuration);
  } else {
    recommendedDuration = Math.ceil((minDuration + 1) / 5) * 5;
  }

  return {
    total_questions: questions.length,
    minimum_duration_minutes: minDuration,
    recommended_duration_minutes: recommendedDuration,
    breakdown: {
      coding_questions: codingCount,
      coding_allocated_minutes: codingMinutes,
      mcq_questions: mcqCount,
      concept_questions: conceptCount,
      behavioral_questions: behavioralCount,
      general_questions: generalCount,
      theoretical_questions: conceptCount + behavioralCount + generalCount,
      transition_buffer_minutes: SESSION_TRANSITION_BUFFER_MINUTES,
      exact_required_minutes: Math.round(totalTime * 10) / 10,
    },
  };
}

/**
 * Returns a human-friendly breakdown string listing only non-zero question categories.
 * Example: "1 Concept, 4 Behavioral + 2.5m buffer"
 */
export function formatDurationBreakdownText(breakdown, totalQuestions, minDuration) {
  if (!breakdown || totalQuestions === 0) return '';

  const parts = [];
  if (breakdown.coding_questions > 0) {
    parts.push(`${breakdown.coding_questions} Coding (${breakdown.coding_allocated_minutes}m)`);
  }
  if (breakdown.mcq_questions > 0) {
    parts.push(`${breakdown.mcq_questions} MCQ`);
  }
  if (breakdown.concept_questions > 0) {
    parts.push(`${breakdown.concept_questions} Concept`);
  }
  if (breakdown.behavioral_questions > 0) {
    parts.push(`${breakdown.behavioral_questions} Behavioral`);
  }
  if (breakdown.general_questions > 0) {
    parts.push(`${breakdown.general_questions} General`);
  }
  if (parts.length === 0 && breakdown.theoretical_questions > 0) {
    parts.push(`${breakdown.theoretical_questions} Concept`);
  }

  const breakdownSummary = parts.length > 0 ? parts.join(', ') : `${totalQuestions} questions`;
  const bufferText = breakdown.transition_buffer_minutes ? ` + ${breakdown.transition_buffer_minutes}m buffer` : '';

  return `Based on ${totalQuestions} questions: ${breakdownSummary}${bufferText}. Interviews for this role cannot be scheduled below ${minDuration} mins to ensure candidates can attempt every question.`;
}
